/**
 * Occupation Form Component
 * Reusable form for creating and editing occupations
 */

import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  FormHelperText,
  Typography,
  Divider,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  SpecialActivityType,
  SPECIAL_ACTIVITIES,
  OccupationSpecialActivity
} from '../../../services/occupationService';
import { CompanySelectionModal, CompanyInfoBox } from '../../../components/forms';
import { useCompanySelection } from '../../../hooks/useCompanySelection';
import { CampoCbo } from './CampoCbo';

// Form uses object internally for switches, converts to array before submit
export interface SpecialActivitiesFormObject {
  [SpecialActivityType.HEIGHT_WORK]?: boolean;
  [SpecialActivityType.CONFINED_SPACE]?: boolean;
  [SpecialActivityType.ELECTRICAL]?: boolean;
  [SpecialActivityType.FORKLIFT]?: boolean;
  [SpecialActivityType.FOOD_HANDLING]?: boolean;
  [SpecialActivityType.PEMT]?: boolean;
  [SpecialActivityType.HOT_WORK]?: boolean;
  [SpecialActivityType.SELF_PROPELLED_MACHINE]?: boolean;
  [SpecialActivityType.VEHICLE_DRIVING]?: boolean;
  [SpecialActivityType.RESPIRATORY_PROTECTION]?: boolean;
  [SpecialActivityType.IONIZING_RADIATION]?: boolean;
  [SpecialActivityType.EMERGENCY_BRIGADE]?: boolean;
  [SpecialActivityType.HYPERBARIC_WORK]?: boolean;
  [SpecialActivityType.CHAINSAW_OPERATION]?: boolean;
}

// Types
export interface OccupationFormData {
  name: string;
  cboCode?: string;
  description?: string;
  role?: string;
  departmentId: number | string;
  companyId: number | string;
  specialActivities?: SpecialActivitiesFormObject;
}

interface Occupation {
  id: number;
  name: string;
  cboCode?: string;
  description?: string;
  role?: string;
  departmentId: number;
  companyId: number;
  specialActivities?: OccupationSpecialActivity[];
}

// Form validation schema
const occupationSchema = yup.object({
  name: yup
    .string()
    .required('Nome do cargo é obrigatório')
    .min(2, 'Mínimo 2 caracteres'),
  cboCode: yup
    .string()
    .notRequired()
    .test('cbo-format', 'Formato inválido. Use XXXX-XX', function(value) {
      if (!value) return true; // Campo opcional
      // Aceita formato completo XXXX-XX ou parcial durante digitação
      return /^\d{0,4}(-\d{0,2})?$/.test(value) || /^\d{4}-\d{2}$/.test(value);
    }),
  description: yup.string().notRequired(),
  role: yup.string().notRequired(),
  companyId: yup
    .mixed()
    .required('Empresa é obrigatória')
    .test('is-valid', 'Empresa inválida', value => value && value !== ''),
  departmentId: yup
    .mixed()
    .required('Departamento é obrigatório')
    .test('is-valid', 'Departamento inválido', value => value && value !== ''),
}).required();

interface OccupationFormProps {
  occupation?: Occupation;
  onSubmit: (data: OccupationFormData) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  error?: string | null;
  mode?: 'create' | 'edit';
  companies?: Array<{ id: number; tradeName: string; legalName: string }>;
  departments?: Array<{ id: number; name: string; companyId: number }>;
}

const OccupationForm: React.FC<OccupationFormProps> = ({
  occupation,
  onSubmit,
  onCancel,
  loading = false,
  error,
  mode = 'create',
  companies: _companies = [],
  departments = [],
}) => {
  const {
    modalOpen,
    selectedCompany,
    showInfoBox,
    openModal,
    handleSelect: handleCompanySelect,
  } = useCompanySelection({ isEditMode: mode === 'edit', currentCompanyId: String(occupation?.companyId ?? '') });
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<OccupationFormData>({
    resolver: yupResolver(occupationSchema) as any,
    defaultValues: {
      name: '',
      cboCode: '',
      description: '',
      role: '',
      companyId: '',
      departmentId: '',
      specialActivities: {
        [SpecialActivityType.HEIGHT_WORK]: false,
        [SpecialActivityType.CONFINED_SPACE]: false,
        [SpecialActivityType.ELECTRICAL]: false,
        [SpecialActivityType.FORKLIFT]: false,
        [SpecialActivityType.FOOD_HANDLING]: false,
        [SpecialActivityType.PEMT]: false,
        [SpecialActivityType.HOT_WORK]: false,
        [SpecialActivityType.SELF_PROPELLED_MACHINE]: false,
        [SpecialActivityType.VEHICLE_DRIVING]: false,
        [SpecialActivityType.RESPIRATORY_PROTECTION]: false,
        [SpecialActivityType.IONIZING_RADIATION]: false,
        [SpecialActivityType.EMERGENCY_BRIGADE]: false,
        [SpecialActivityType.HYPERBARIC_WORK]: false,
        [SpecialActivityType.CHAINSAW_OPERATION]: false,
      },
    },
  });

  const selectedCompanyId = watch('companyId');

  // Filter departments based on selected company
  const filteredDepartments = React.useMemo(() => {
    if (!selectedCompanyId) {
      return [];
    }
    return departments.filter(dept => dept.companyId === Number(selectedCompanyId));
  }, [selectedCompanyId, departments]);

  // Reset departmentId when company changes
  useEffect(() => {
    if (mode === 'create') {
      setValue('departmentId', '');
    }
  }, [selectedCompanyId, setValue, mode]);

  // Sync company selection (from modal or auto-fill) to form
  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      const currentValue = watch('companyId');
      if (String(currentValue) !== selectedCompany.id) {
        setValue('companyId', selectedCompany.id);
      }
    }
  }, [selectedCompany, watch, setValue]);

  // Load occupation data when editing
  useEffect(() => {
    if (occupation && mode === 'edit') {
      // Convert array to object for form
      const activitiesObject: SpecialActivitiesFormObject = {
        [SpecialActivityType.HEIGHT_WORK]: false,
        [SpecialActivityType.CONFINED_SPACE]: false,
        [SpecialActivityType.ELECTRICAL]: false,
        [SpecialActivityType.FORKLIFT]: false,
        [SpecialActivityType.FOOD_HANDLING]: false,
        [SpecialActivityType.PEMT]: false,
        [SpecialActivityType.HOT_WORK]: false,
        [SpecialActivityType.SELF_PROPELLED_MACHINE]: false,
        [SpecialActivityType.VEHICLE_DRIVING]: false,
        [SpecialActivityType.RESPIRATORY_PROTECTION]: false,
        [SpecialActivityType.IONIZING_RADIATION]: false,
        [SpecialActivityType.EMERGENCY_BRIGADE]: false,
        [SpecialActivityType.HYPERBARIC_WORK]: false,
        [SpecialActivityType.CHAINSAW_OPERATION]: false,
      };

      // Set true for activities present in array
      if (occupation.specialActivities) {
        occupation.specialActivities.forEach(item => {
          activitiesObject[item.activity] = true;
        });
      }

      reset({
        name: occupation.name ?? '',
        cboCode: occupation.cboCode ?? '',
        description: occupation.description ?? '',
        role: occupation.role ?? '',
        companyId: occupation.companyId,
        departmentId: occupation.departmentId,
        specialActivities: activitiesObject,
      });
    }
  }, [occupation, mode, reset]);

  // Form submission handler
  const onFormSubmit = async (data: OccupationFormData) => {
    try {
      await onSubmit(data);
    } catch (err) {
      console.error('Form submission error:', err);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      <Grid container spacing={3}>
        {/* Basic Information Section */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Informações Básicas
          </Typography>
          <Divider />
        </Grid>

        {/* Occupation Name */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Nome do Cargo"
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={loading}
              />
            )}
          />
        </Grid>

        {/* CBO Code */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="cboCode"
            control={control}
            render={({ field }) => (
              <CampoCbo
                valor={field.value ?? ''}
                onChange={field.onChange}
                onEscolher={(ocupacao) => {
                  // O título oficial só entra quando o nome ainda está vazio: nome digitado é da empresa.
                  if (!watch('name')?.trim()) setValue('name', ocupacao.titulo, { shouldValidate: true });
                }}
                erro={errors.cboCode?.message}
                disabled={loading}
              />
            )}
          />
        </Grid>

        {/* Role/Function */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="role"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Função/Atribuição"
                fullWidth
                placeholder="Ex: Supervisor, Operador, Técnico"
                disabled={loading}
              />
            )}
          />
        </Grid>

        {/* Description */}
        <Grid size={12}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Descrição do Cargo"
                fullWidth
                multiline
                rows={3}
                placeholder="Descreva as principais responsabilidades e atividades"
                disabled={loading}
              />
            )}
          />
        </Grid>

        {/* Company and Department Section */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.5rem' }}>
            Vínculo Organizacional
          </Typography>
          <Divider />
        </Grid>

        {/* Department */}
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Controller
            name="departmentId"
            control={control}
            render={({ field }) => (
              <FormControl 
                fullWidth 
                required 
                error={!!errors.departmentId} 
                disabled={loading || !selectedCompanyId}
              >
                <InputLabel>Departamento</InputLabel>
                <Select {...field} label="Departamento">
                  <MenuItem value="">Selecione...</MenuItem>
                  {filteredDepartments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.departmentId && (
                  <FormHelperText>{errors.departmentId.message}</FormHelperText>
                )}
                {!selectedCompanyId && (
                  <FormHelperText>Selecione uma empresa primeiro</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        {/* Special Activities Section */}
        <Grid size={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2, fontSize: '1.5rem' }}>
            Atividades Especiais (NR)
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Selecione as atividades especiais que se aplicam a este cargo
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
              {SPECIAL_ACTIVITIES.map((activity) => (
                <Grid
                  key={activity.value}
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Controller
                    name={`specialActivities.${activity.value}` as any}
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value || false}
                            onChange={field.onChange}
                            disabled={loading}
                          />
                        }
                        label={activity.label}
                      />
                    )}
                  />
                </Grid>
              ))}
            </Grid>
        </Grid>

        {/* Form Actions */}
        <Grid size={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            {onCancel && (
              <Button
                variant="outlined"
                onClick={onCancel}
                disabled={loading}
              >
                Cancelar
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              {loading ? 'Salvando...' : mode === 'create' ? 'Criar Cargo' : 'Salvar Alterações'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    {showInfoBox && selectedCompany && (
      <CompanyInfoBox
        companyName={selectedCompany.name}
        onEdit={openModal}
      />
    )}
    <CompanySelectionModal
      open={modalOpen}
      onSelect={handleCompanySelect}
    />
    </Box>
  );
};

export default OccupationForm;
